/**
 * Yasal metinler — müşterinin elektronik onayı (2026-10-23).
 *
 * Müşteri girişten sonra bir kez görür: KVKK aydınlatma metni ("Okudum") + açık rızalar (Onaylıyorum /
 * Onaylamıyorum). Her metin için bir kez sorulur; metin değişirse yalnız o metin yeniden sorulur. Personel ve
 * "müşteri olarak görüntüle" oturumunda sunucu boş liste döner (routers/panel/yasal.py). Rızayı reddetmek paneli
 * kilitlemez; karar "Yasal Metinler" sayfasından değiştirilebilir. Sunucuya ulaşılamazsa pencere açılmaz.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { ShieldCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api/panel/yasal`;
const GIZLI_YOLLAR = ['/login', '/odeme-sonuc', '/panel/odeme-sonuc', '/aktivasyon', '/wms/giris'];
const MUSTERI_ROLLERI = ['client', 'musteri'];

export default function YasalOnayModal() {
  const { user, token } = useAuth();
  const location = useLocation();
  const [bekleyen, setBekleyen] = useState([]);
  const [kararlar, setKararlar] = useState({});
  const [gonderiliyor, setGonderiliyor] = useState(false);

  const musteri = !!user && MUSTERI_ROLLERI.includes((user.role || '').toLowerCase());
  const gizli = GIZLI_YOLLAR.some((y) => location.pathname === y || location.pathname.startsWith(`${y}/`));

  const yukle = useCallback(async () => {
    if (!token || !musteri) { setBekleyen([]); return; }
    try {
      const { data } = await axios.get(`${API}/durum`, { headers: { Authorization: `Bearer ${token}` } });
      setBekleyen(data?.bekleyen || []);
      setKararlar({});
    } catch {
      setBekleyen([]);            // ulaşılamazsa paneli bekletme
    }
  }, [token, musteri]);

  useEffect(() => { yukle(); }, [yukle]);

  if (!bekleyen.length || gizli) return null;

  const tamam = bekleyen.every((m) => kararlar[m.kod]);
  const sec = (kod, karar) => setKararlar((k) => ({ ...k, [kod]: karar }));

  const kaydet = async () => {
    if (!tamam || gonderiliyor) return;
    setGonderiliyor(true);
    try {
      await axios.post(`${API}/onay`, {
        kararlar: bekleyen.map((m) => ({ kod: m.kod, surum: m.surum, karar: kararlar[m.kod] })),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Tercihleriniz kaydedildi');
      setBekleyen([]);
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error('Metinlerden biri güncellendi. Güncel hâli gösteriliyor, lütfen tekrar okuyun.');
        yukle();
      } else {
        toast.error('Kaydedilemedi. Bağlantınızı kontrol edip tekrar deneyin.');
      }
    } finally {
      setGonderiliyor(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[95] bg-slate-900/60 flex items-center justify-center p-3 sm:p-6" role="dialog"
      aria-modal="true" aria-labelledby="yasal-baslik">
      <div className="bg-white w-full max-w-3xl max-h-[92vh] rounded-xl shadow-2xl flex flex-col">
        <div className="px-5 sm:px-7 pt-5 pb-4 border-b border-slate-200">
          <div className="flex items-center gap-2 text-slate-900">
            <ShieldCheck size={22} className="text-emerald-600 shrink-0" aria-hidden="true" />
            <h2 id="yasal-baslik" className="text-lg font-semibold">Kişisel verileriniz hakkında</h2>
          </div>
          <p className="text-sm text-slate-600 mt-1.5">
            Paneli kullanmaya devam etmeden önce aşağıdaki metinleri okuyup her biri için seçiminizi yapın.
            Bu ekran yalnızca bir kez gösterilir; seçimlerinizi daha sonra <b>Yasal Metinler</b> sayfasından değiştirebilirsiniz.
          </p>
        </div>

        <div className="overflow-y-auto px-5 sm:px-7 py-4 space-y-5">
          {bekleyen.map((m) => (
            <section key={m.kod} className="border border-slate-200 rounded-lg">
              <h3 className="px-4 pt-3 font-semibold text-slate-900 text-[15px]">{m.baslik}</h3>
              <div className="yasal-metin max-h-56 overflow-y-auto px-4 pb-2 mt-1"
                dangerouslySetInnerHTML={{ __html: m.html }} />
              <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 rounded-b-lg flex flex-wrap gap-x-6 gap-y-2">
                {m.tur === 'bilgi' ? (
                  <label className="inline-flex items-center gap-2 text-sm text-slate-800 cursor-pointer">
                    <input type="checkbox" className="h-4 w-4 accent-emerald-600" checked={kararlar[m.kod] === 'okudum'}
                      onChange={(e) => sec(m.kod, e.target.checked ? 'okudum' : undefined)} />
                    Okudum, anladım.
                  </label>
                ) : (
                  [['onay', 'Onaylıyorum'], ['ret', 'Onaylamıyorum']].map(([deger, etiket]) => (
                    <label key={deger} className="inline-flex items-center gap-2 text-sm text-slate-800 cursor-pointer">
                      <input type="radio" name={`yasal-${m.kod}`} className="h-4 w-4 accent-emerald-600"
                        checked={kararlar[m.kod] === deger} onChange={() => sec(m.kod, deger)} />
                      {etiket}
                    </label>
                  ))
                )}
              </div>
            </section>
          ))}
        </div>

        <div className="px-5 sm:px-7 py-4 border-t border-slate-200 flex flex-col-reverse sm:flex-row sm:items-center gap-3 sm:justify-between">
          <span className="text-xs text-slate-500">Seçimleriniz tarih ve saatiyle kaydedilir.</span>
          <button type="button" onClick={kaydet} disabled={!tamam || gonderiliyor}
            className="w-full sm:w-auto px-5 py-2.5 rounded-lg bg-emerald-600 text-white font-medium text-sm hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed">
            {gonderiliyor ? 'Kaydediliyor…' : tamam ? 'Kaydet ve devam et' : `${bekleyen.length - Object.values(kararlar).filter(Boolean).length} seçim kaldı`}
          </button>
        </div>
      </div>
    </div>
  );
}
